import { Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import SignupFields, { emptySignupForm } from "@/components/SignupFields";
import CryptoConverter from "@/components/CryptoConverter";
import PriceChart from "@/components/PriceChart";
import { ArrowRight, ShieldCheck, Search, HandCoins, Lock, TrendingUp, FileCheck } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Landing() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptySignupForm());
  const [submitting, setSubmitting] = useState(false);
  const [showReturn, setShowReturn] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await register(form);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Account created. Welcome to Crypto Asset Recovery.");
      navigate("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <AppHeader />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1621761191319-c6fb62004040?auto=format&fit=crop&w=1920&q=70)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#030303]/70 via-[#030303]/90 to-[#030303]" />
        <div className="absolute inset-0 bg-grain opacity-40 pointer-events-none" />
        {/* Floating crypto asset visuals */}
        <img
          src="/imagery/wallet.webp"
          alt=""
          className="hidden lg:block absolute right-[4%] top-24 w-40 xl:w-52 opacity-70 pointer-events-none"
          style={{ transform: "rotate(-8deg)", filter: "drop-shadow(0 22px 26px rgba(0,0,0,0.5))" }}
        />
        <img
          src="/imagery/hdd-open.webp"
          alt=""
          className="hidden lg:block absolute left-[3%] bottom-16 w-36 xl:w-44 opacity-60 pointer-events-none"
          style={{ transform: "rotate(-12deg)", filter: "drop-shadow(0 22px 26px rgba(0,0,0,0.5))" }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-32 grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-3 space-y-6 sm:space-y-8">
            <div className="inline-flex items-center gap-2 border border-[#FFD700]/40 px-3 py-1.5 text-[10px] sm:text-xs uppercase tracking-widest font-mono text-[#FFD700]">
              <span className="w-1.5 h-1.5 bg-[#FFD700] animate-pulse" />
              {t("landing.badge")}
            </div>

            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.05] sm:leading-[0.95] tracking-tighter">
              <span className="font-light text-white/60">{t("landing.title1")}</span>
              <br />
              <span className="font-black">{t("landing.title2")}</span>{" "}
              <span className="font-black text-[#FFD700]">{t("landing.title3")}</span>
            </h1>

            <p className="text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
              {t("landing.subtitle")}
            </p>

            <div className="flex flex-wrap gap-6 sm:gap-8 pt-2 sm:pt-4">
              <Stat value="€47M+" label={t("landing.stat.recovered")} />
              <Stat value="2,140" label={t("landing.stat.cases")} />
              <Stat value="94%" label={t("landing.stat.success")} />
            </div>
          </div>

          {/* SIGNUP CARD */}
          <div className="lg:col-span-2">
            <div className="bg-[#0A0A0A] border border-white/10 p-8 relative">
              <div className="absolute -top-3 left-8 bg-[#FFD700] text-black text-xs px-2 py-1 font-mono font-bold uppercase tracking-wider">
                {t("signup.tag")}
              </div>
              <h2 className="font-display text-3xl font-black tracking-tighter mb-1">
                {t("signup.title")}
              </h2>
              <p className="text-sm text-white/50 mb-6">
                {t("signup.subtitle")}
              </p>

              <form onSubmit={handleSignup} className="space-y-4">
                <SignupFields form={form} setForm={setForm} prefix="signup" stacked />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="signup-submit-btn"
                  className="w-full bg-[#FFD700] hover:bg-[#F4C430] disabled:opacity-50 text-black font-semibold py-3 flex items-center justify-center gap-2 transition-colors"
                >
                  {submitting ? t("signup.submitting") : t("signup.submit")}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-xs text-white/50 text-center pt-2">
                  {t("signup.hasAccount")}{" "}
                  <Link
                    to="/login"
                    data-testid="landing-login-link"
                    className="text-[#FFD700] hover:underline"
                  >
                    {t("signup.loginLink")}
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-white/10 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-3 gap-8 sm:gap-12 items-start">
            <div className="lg:col-span-1">
              <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700] mb-3">
                {t("process.tag")}
              </p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-4">
                {t("process.title")}
              </h2>
              <p className="text-white/60">
                {t("process.subtitle")}
              </p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-3 gap-4 sm:gap-6">
              <Step
                num="01"
                icon={<Search />}
                title={t("process.trace.title")}
                desc={t("process.trace.desc")}
                href="https://support.blockchain.com/hc/en-us/sections/13676269631132-Account-and-Crypto-Recovery-Assistance"
              />
              <Step
                num="02"
                icon={<FileCheck />}
                title={t("process.freeze.title")}
                desc={t("process.freeze.desc")}
                href="https://support.blockchain.com/hc/en-us/articles/8219099905436-Crypto-Recovery-for-Unsupported-or-Incorrect-Transfers"
              />
              <Step
                num="03"
                icon={<HandCoins />}
                title={t("process.return.title")}
                desc={t("process.return.desc")}
                onClick={() => setShowReturn(true)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST GRID */}
      <section className="border-b border-white/10 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-4 sm:gap-6">
          <Feature
            icon={<Lock />}
            title={t("trust.custody.title")}
            desc={t("trust.custody.desc")}
          />
          <Feature
            icon={<TrendingUp />}
            title={t("trust.dashboard.title")}
            desc={t("trust.dashboard.desc")}
          />
        </div>
      </section>

      {/* LIVE MARKET CHART */}
      <section
        data-testid="landing-price-chart"
        className="border-t border-white/10 py-16 sm:py-24"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="mb-8 sm:mb-10">
            <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700] mb-3">
              {t("chart.tag")}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-3">
              {t("chart.title")}
            </h2>
            <p className="text-white/60 text-sm max-w-2xl">{t("chart.subtitle")}</p>
          </div>
          <PriceChart />
        </div>
      </section>

      {/* CONVERTER */}
      <CryptoConverter />

      {/* FOOTER */}
      <footer className="py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row justify-between gap-4 items-center text-xs text-white/40 font-mono uppercase tracking-widest">
          <span>{t("footer.copyright", { year: new Date().getFullYear() })}</span>
          <span>{t("footer.compliance")}</span>
        </div>
      </footer>

      {showReturn && <ReturnModal onClose={() => setShowReturn(false)} />}
    </div>
  );
}

function ReturnModal({ onClose }) {
  const { t } = useLang();
  const exchanges = [
    {
      name: "Revolut",
      url: "https://www.revolut.com/",
      color: "#191C1F",
      desc: "Send recovered funds directly to your Revolut EUR wallet. Instant fiat access, no bank wire delay.",
      logo: "https://cdn.simpleicons.org/revolut/ffffff",
    },
    {
      name: "Coinbase",
      url: "https://www.coinbase.com/",
      color: "#0052FF",
      desc: "Preferred US-regulated exchange for BTC and ETH withdrawals. IRS-compliant reporting.",
      logo: "https://cdn.simpleicons.org/coinbase/ffffff",
    },
    {
      name: "Binance",
      url: "https://www.binance.com/",
      color: "#F0B90B",
      desc: "Largest global exchange — supports 350+ assets and one of the deepest liquidity pools worldwide.",
      logo: "https://cdn.simpleicons.org/binance/000000",
    },
  ];
  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center px-4 py-8 overflow-y-auto">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-2xl w-full p-8 relative">
        <button
          onClick={onClose}
          data-testid="return-modal-close-btn"
          className="absolute top-4 right-4 p-1 hover:bg-white/10"
          aria-label="Close"
        >
          <span className="text-xl leading-none">×</span>
        </button>
        <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700] mb-2">
          / 003 — Return
        </p>
        <h2 className="font-display text-3xl font-black tracking-tighter mb-2">
          {t("return.title")}
        </h2>
        <p className="text-white/60 mb-6 text-sm">
          {t("return.subtitle")}
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          {exchanges.map((e) => (
            <a
              key={e.name}
              href={e.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`return-exchange-${e.name.toLowerCase()}`}
              className="border border-white/10 hover:border-[#FFD700]/50 p-5 transition-colors block group"
            >
              <div
                className="w-14 h-14 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform"
                style={{ backgroundColor: e.color }}
              >
                <img src={e.logo} alt={e.name} className="w-8 h-8" />
              </div>
              <div className="font-display text-lg font-bold tracking-tight mb-2">
                {e.name}
              </div>
              <p className="text-xs text-white/60 leading-relaxed">{e.desc}</p>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display text-4xl font-black tracking-tighter text-[#FFD700]">{value}</div>
      <div className="text-xs uppercase tracking-widest text-white/50 font-mono mt-1">{label}</div>
    </div>
  );
}

function Step({ num, icon, title, desc, href, onClick }) {
  const shared = (
    <>
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-xs text-white/40 tracking-widest">{num}</span>
        <span className="text-[#FFD700]">{icon}</span>
      </div>
      <h3 className="font-display text-2xl font-black tracking-tighter mb-2">{title}</h3>
      <p className="text-sm text-white/60 leading-relaxed">{desc}</p>
    </>
  );
  const cls =
    "bg-[#0A0A0A] border border-white/10 p-6 hover:border-[#FFD700]/40 transition-colors h-full block";
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`step-${title.toLowerCase()}-link`}
        className={cls + " cursor-pointer"}
      >
        {shared}
      </a>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={`step-${title.toLowerCase()}-btn`}
        className={cls + " text-left w-full cursor-pointer"}
      >
        {shared}
      </button>
    );
  }
  return <div className={cls}>{shared}</div>;
}

function Feature({ icon, title, desc }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-8 hover:border-white/30 transition-colors">
      <div className="text-[#FFD700] mb-4">{icon}</div>
      <h3 className="font-display text-xl font-bold tracking-tight mb-2">{title}</h3>
      <p className="text-sm text-white/60 leading-relaxed">{desc}</p>
    </div>
  );
}
